/**
 * Date, orari, durate e numeri in italiano per le schermate e-commerce. Modulo puro.
 * Sempre nel fuso di Roma (regola del progetto), mai quello del browser o del server.
 */

const TZ = 'Europe/Rome';

const parts = (ms: number) => {
  const f = new Intl.DateTimeFormat('it-IT', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(new Date(ms))) out[p.type] = p.value;
  // alcuni motori scrivono "24" per la mezzanotte
  if (out.hour === '24') out.hour = '00';
  return out as { year: string; month: string; day: string; hour: string; minute: string };
};

const toMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

const dayKey = (ms: number) => {
  const p = parts(ms);
  return `${p.year}-${p.month}-${p.day}`;
};

/** "adesso", "3 min fa", "5 ore fa", "ieri alle 18:10", "20/09 alle 09:15". */
export function relativeTimeIt(iso: string | null | undefined, now: number = Date.now()): string {
  const ms = toMs(iso);
  if (ms === null) return 'mai';
  const diff = now - ms;
  if (diff < 60_000) return 'adesso';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min} min fa`;
  const today = dayKey(now);
  if (dayKey(ms) === today) {
    const h = Math.floor(min / 60);
    return h === 1 ? '1 ora fa' : `${h} ore fa`;
  }
  const p = parts(ms);
  if (dayKey(ms) === dayKey(now - 86_400_000)) return `ieri alle ${p.hour}:${p.minute}`;
  return `${p.day}/${p.month} alle ${p.hour}:${p.minute}`;
}

/** "25/09/2026 18:10". */
export function formatDateTimeIt(iso: string | null | undefined): string {
  const ms = toMs(iso);
  if (ms === null) return '';
  const p = parts(ms);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** "25/09/2026". */
export function formatDateIt(iso: string | null | undefined): string {
  const ms = toMs(iso);
  if (ms === null) return '';
  const p = parts(ms);
  return `${p.day}/${p.month}/${p.year}`;
}

/** "18:10". */
export function formatTimeIt(iso: string | null | undefined): string {
  const ms = toMs(iso);
  if (ms === null) return '';
  const p = parts(ms);
  return `${p.hour}:${p.minute}`;
}

/** "25 settembre 2026". */
export function formatDateLongIt(iso: string | null | undefined): string {
  const ms = toMs(iso);
  if (ms === null) return '';
  return new Intl.DateTimeFormat('it-IT', { timeZone: TZ, day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(ms));
}

/** "6 min", "1 h 5 min", "meno di 1 min". */
export function formatDurationIt(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'meno di 1 min';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}

/** "1.240": separatore delle migliaia sempre col punto (anche sotto 10.000). */
export function formatCountIt(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const s = Math.trunc(n).toString();
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}
